import {
  IBasicGameOptions,
  IEvent,
  IGame,
  ISportEventContentBlock,
  ISportEventPlayer,
  ITeam,
  ITimeBasedGameStateInfo
} from "@webng-types/write-model";
import {ensureTimedGameOptions, formatDisplayTimeSegment} from "./timingUtils";
import {SportstypeSpec} from "./types";
import {sportI18nKey} from "./i18nUtils";
import {TFunction} from "i18next";


type TimingFields = Pick<ISportEventContentBlock, "time_of_play"|"phase">

function convertTimeBasedGameStateInfo(stateInfo: ITimeBasedGameStateInfo, options: IBasicGameOptions|undefined): TimingFields|undefined {
  if(stateInfo.check_seconds !== undefined && !stateInfo.is_break && stateInfo.gamestate === 1) {
    const timedOptions = ensureTimedGameOptions(options)
    if(timedOptions) {

      const recurrence = stateInfo.recurrence || 1
      const {length, offset} = recurrence <= timedOptions.num_regular_phases ?
        ({
          length: timedOptions.regular_phase_length * 60,
          offset: (recurrence - 1) * timedOptions.regular_phase_length * 60
        }) :
        ({
          length: timedOptions.overtime_phase_length * 60,
          offset: (timedOptions.num_regular_phases * timedOptions.regular_phase_length * 60 + (recurrence - timedOptions.num_regular_phases - 1) * timedOptions.overtime_phase_length * 60)
        })


      switch (timedOptions.timing_type) {
        case 'u':
          return {
            time_of_play: formatDisplayTimeSegment("M:ss", stateInfo.check_seconds)
          }
        case 'd':
          return {
            time_of_play: formatDisplayTimeSegment("M:ss", stateInfo.check_seconds)
          }
        case 'au':
          return {
            time_of_play: formatDisplayTimeSegment('M.', offset + stateInfo.check_seconds)
          }
        case 'aui':
          return {
            time_of_play: stateInfo.check_seconds > length ?
              `${formatDisplayTimeSegment('M\'', offset + length)}+${formatDisplayTimeSegment('M', stateInfo.check_seconds - length)}` :
              formatDisplayTimeSegment('M\'', offset + stateInfo.check_seconds)
          }
      }
    }
  }

  return undefined
}

function getTeam(game: IGame, homeTeam: boolean|undefined): ITeam|undefined {
  if(homeTeam !== undefined) {
    if (game.scoreboard && game.scoreboard._type === 'Tik::Model::Content::Scoreboard::TeamGameScoreboard') {
      if (homeTeam) {
        return game.scoreboard.home_team
      } else {
        return game.scoreboard.away_team
      }
    } else if (game.scoreboard && game.scoreboard._type === 'Tik::Model::Content::Scoreboard::SetBasedScoreboard') {
      if (homeTeam) {
        return game.scoreboard.home_team
      } else {
        return game.scoreboard.away_team
      }
    } else if (game.meta_info && game.meta_info._type === 'Tik::Model::GameMetaInfo::TeamGameMetaInfo') {
      if (homeTeam) {
        return game.meta_info.home_team
      } else {
        return game.meta_info.away_team
      }
    }
  }

  return undefined
}


export function legacySportEventInfoConverter(sportstype: SportstypeSpec, t: TFunction, game: IGame, event: IEvent): ISportEventContentBlock|undefined {
  // const sportstype = Sportstypes.find(s => s.id === game.sportstype)
  let timingFields: TimingFields|undefined = undefined

  switch (event.state_info?._type) {
    case 'Tik::Model::GameStateInfo::TimeBasedGameStateInfo':
      timingFields = convertTimeBasedGameStateInfo(event.state_info, game.options)
      break;
  }

  let eventFields: Pick<ISportEventContentBlock, "event_type"|"team"|"players"|"title">|undefined = undefined

  if(event.event_info?.event_type && sportstype.legacyEventMap) {
    const eventType =  sportstype.legacyEventMap[event.event_info.event_type]
    const eventSpec = sportstype.events.find(e => e.id === eventType)

    if(eventType && eventSpec) {
      const path = `${sportstype.id}.event.${eventType}`
      const name = t(sportI18nKey(path, 'name'))

      const players: ISportEventPlayer[] = []

      if(eventSpec.players && eventSpec.players.length > 0 && event.event_info.player) {
        players.push({
          _type: 'Tik::Model::Content::SportEventPlayer',
          player: event.event_info.player,
          event_player_title: t(sportI18nKey(`${sportstype.id}.event-player.${eventSpec.players[0].id}`, 'name')),
          event_player_type: eventSpec.players[0].id
        })
      }
      if(eventSpec.players && eventSpec.players.length > 1 && event.event_info.other_player) {
        players.push({
          _type: 'Tik::Model::Content::SportEventPlayer',
          player: event.event_info.other_player,
          event_player_title: t(sportI18nKey(`${sportstype.id}.event-player.${eventSpec.players[1].id}`, 'name')),
          event_player_type: eventSpec.players[1].id
        })
      }

      eventFields = {
        event_type: game.sportstype + "." + eventType,
        team: eventSpec.team === 'any' ? getTeam(game, event.event_info.is_home_team) : undefined,
        title: name,
        players
      }
    }
  }

  if(timingFields || eventFields) {
    return {
      _type: "Tik::Model::Content::SportEventContentBlock",
      sportstype: sportstype.id,
      local_id: 'legacy-sports',
      ...(timingFields || {}),
      ...(eventFields || {})
    }
  } else {
    return undefined
  }
}
